import type { HttpLayer } from '../http.js';
import type {
  AccountSecret,
  CreateAccountSecretRequest,
  DeleteAccountSecretResponse,
  ListAccountSecretsResponse,
  ListSecretsParams,
  RevealSecretResponse,
  UpdateAccountSecretRequest,
} from '../types/secrets.js';

/**
 * Account-global secrets — named encrypted values shared by every session on the
 * account, with or without a workspace. Names and descriptions are searchable;
 * the value is returned only by `reveal`.
 */
export class SecretsResource {
  constructor(private http: HttpLayer) {}

  private prefix = '/v1/secrets';

  /**
   * List the account's secrets, masked. Values are never returned here.
   * @param params.workspace_id Narrows the listing to secrets carrying this tag; it does not scope access.
   * @returns Every secret on the account, ordered by name.
   */
  async list(params?: ListSecretsParams): Promise<ListAccountSecretsResponse> {
    const query = new URLSearchParams();
    if (params?.workspace_id) query.set('workspace_id', params.workspace_id);
    if (params?.limit !== undefined) query.set('limit', String(params.limit));
    const suffix = query.toString();
    return this.http.get(suffix ? `${this.prefix}?${suffix}` : this.prefix);
  }

  /**
   * Fetch one secret's metadata, masked.
   * @param secretId The secret's id.
   * @returns The secret without its value.
   */
  async get(secretId: string): Promise<AccountSecret> {
    return this.http.get(`${this.prefix}/${secretId}`);
  }

  /**
   * Create an account-global secret.
   * @param params.name Uppercase letters, digits and underscore, starting with a letter.
   * @param params.value The value to encrypt at rest.
   * @returns The created secret, masked.
   */
  async create(params: CreateAccountSecretRequest): Promise<AccountSecret> {
    return this.http.post(this.prefix, params);
  }

  /**
   * Replace a secret's value, description or workspace tag.
   * @param secretId The secret's id.
   * @param params.value Omit to keep the current value.
   * @returns The updated secret, masked.
   */
  async update(secretId: string, params: UpdateAccountSecretRequest): Promise<AccountSecret> {
    return this.http.patch(`${this.prefix}/${secretId}`, params);
  }

  /**
   * Return one secret's plaintext value. Authorized exactly as `get` is — there
   * is no per-secret governance.
   * @param secretId The secret's id.
   * @returns The secret and its decrypted value.
   */
  async reveal(secretId: string): Promise<RevealSecretResponse> {
    return this.http.post(`${this.prefix}/${secretId}/reveal`, {});
  }

  /**
   * Permanently delete a secret. The value cannot be recovered.
   * @param secretId The secret's id.
   * @returns The deleted secret's id and name.
   */
  async delete(secretId: string): Promise<DeleteAccountSecretResponse> {
    return this.http.delete(`${this.prefix}/${secretId}`);
  }
}
